/**
 * ユーザー単位の画面で使う Feature 判定。
 *
 * Offer の判定単位はワークスペースだが、企業/団体アカウント管理（/admin）のように
 * ワークスペースに属さない画面には対応する Offer がない。
 * そのため所属ワークスペースのいずれかで ON なら利用可、という最大権限で判定する。
 *
 * 親を持つ子ワークスペースは planId が 'child' のままだと free 相当に落ちるが、
 * その場合も親ワークスペース自体が一覧に含まれていれば ON と判定される。
 */
import { useEffect, useState } from 'react';
import { fetchUserWorkspaces } from '../workspace/fetchUserWorkspaces';
import { resolveEffectiveEntitlements, type FeatureKey } from './entitlements';

export function useFeatureInAnyWorkspace(
  uid: string | undefined,
  feature: FeatureKey
): { enabled: boolean; loading: boolean } {
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!uid) {
      setEnabled(false);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    fetchUserWorkspaces(uid)
      .then((workspaces) => {
        if (cancelled) return;
        setEnabled(
          workspaces.some(
            (ws) =>
              resolveEffectiveEntitlements({
                seriesId: ws.seriesId,
                planId: ws.planId,
                offerId: ws.offerId,
                parentWorkspaceId: ws.parentWorkspaceId,
              }).features[feature].enabled
          )
        );
      })
      .catch((error) => {
        console.error('[useFeatureInAnyWorkspace] 判定に失敗しました', { feature, error });
        if (!cancelled) setEnabled(false);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [uid, feature]);

  return { enabled, loading };
}
